import { useState, useEffect } from 'react';
import { Link, usePage, router } from '@inertiajs/react';
import {
    HomeIcon,
    BuildingOfficeIcon,
    UsersIcon,
    EnvelopeIcon,
    ClipboardDocumentListIcon,
    ChartBarIcon,
    Cog6ToothIcon,
    UserCircleIcon,
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    XMarkIcon,
    ChevronDoubleLeftIcon,
    ChevronDownIcon,
    QrCodeIcon,
    DocumentTextIcon,
    ClipboardDocumentCheckIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    ShieldCheckIcon,
    BellIcon,
    SparklesIcon,
    ClockIcon,
    UserGroupIcon,
    MapPinIcon,
    DocumentChartBarIcon,
    IdentificationIcon,
} from '@heroicons/react/24/outline';

/* =============================================
   NavItem
============================================= */
function NavItem({ href, icon: Icon, label, active = false, collapsed = false }) {
    return (
        <Link
            href={href}
            title={collapsed ? label : undefined}
            className={`group relative flex items-center ${collapsed ? 'justify-center mx-auto w-11 h-11' : 'gap-3 px-3 py-2'} rounded-xl font-medium transition-all duration-200 mb-0.5
                ${active
                    ? 'bg-white text-emerald-700 shadow-md shadow-emerald-900/20'
                    : 'text-emerald-100/80 hover:bg-white/10 hover:text-white'
                }`}
        >
            <Icon className={`flex-shrink-0 ${collapsed ? 'w-5 h-5' : 'w-[18px] h-[18px]'} ${active ? 'text-emerald-600' : ''}`} />
            {!collapsed && <span className="text-[13px] whitespace-nowrap">{label}</span>}
            {collapsed && (
                <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[60] shadow-lg pointer-events-none">
                    {label}
                    <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-slate-900 rotate-45" />
                </div>
            )}
        </Link>
    );
}

/* =============================================
   NavSection
============================================= */
function NavSection({ label, children, defaultOpen = false, collapsed = false }) {
    const [open, setOpen] = useState(defaultOpen);

    useEffect(() => {
        if (defaultOpen) setOpen(true);
    }, [defaultOpen]);

    if (collapsed) return <>{children}</>;

    return (
        <div className="mb-1">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-emerald-300/50 font-medium transition-all duration-200 hover:text-emerald-200/70"
            >
                <span className="text-[10px] uppercase tracking-[0.15em] font-bold">{label}</span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                {children}
            </div>
        </div>
    );
}

/* =============================================
   AppLayout
============================================= */
export default function AppLayout({ children }) {
    const { auth, companySettings } = usePage().props;
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem('sidebarCollapsed') === 'true';
        if (saved && window.innerWidth >= 1024) setCollapsed(true);

        const removeListener = router.on('navigate', () => {
            setSidebarOpen(false);
            setProfileOpen(false);
        });
        return removeListener;
    }, []);

    const toggleCollapse = () => {
        const next = !collapsed;
        setCollapsed(next);
        localStorage.setItem('sidebarCollapsed', String(next));
    };

    const currentUrl = usePage().url;
    const isActive = (pattern) => currentUrl.startsWith(pattern);
    const user = auth?.user;
    const perms = user?.permissions || [];
    const isAdmin = user?.is_admin;
    const can = (slug) => isAdmin || perms.includes(slug);
    const canAny = (...slugs) => isAdmin || slugs.some(s => perms.includes(s));

    return (
        <div className="min-h-screen bg-emerald-50/50">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* ===== SIDEBAR ===== */}
            <aside
                className={`fixed left-0 top-0 h-screen z-40 flex flex-col transition-all duration-300
                    ${collapsed ? 'w-[72px]' : 'w-[260px]'}
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    bg-emerald-800 shadow-2xl shadow-emerald-900/50
                `}
            >
                {/* Decorative top accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-green-300 to-emerald-400" />

                {/* Logo area */}
                <div className={`h-[68px] flex items-center ${collapsed ? 'justify-center px-2' : 'px-5'} border-b border-emerald-700/50`}>
                    <Link href="/dashboard" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-900/30">
                            <span className="text-emerald-700 font-black text-base tracking-tight">PH</span>
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col">
                                <span className="font-bold text-white text-[15px] leading-tight tracking-tight">Pheladelfia</span>
                                <span className="text-[10px] text-emerald-300/70 font-semibold tracking-wide">AGRICULTURAL LLC</span>
                            </div>
                        )}
                    </Link>
                    {!collapsed && (
                        <button onClick={() => setSidebarOpen(false)} className="lg:hidden ml-auto p-2 rounded-lg hover:bg-white/10 transition-colors">
                            <XMarkIcon className="w-5 h-5 text-emerald-200" />
                        </button>
                    )}
                </div>

                {/* Navigation */}
                <nav className={`flex-1 py-4 ${collapsed ? 'px-2.5' : 'px-3'} overflow-y-auto`} style={{ scrollbarWidth: 'none' }}>
                    {can('dashboard.view') && <NavItem href="/dashboard" icon={HomeIcon} label="Dashboard" active={isActive('/dashboard')} collapsed={collapsed} />}

                    <div className="my-3 mx-2 h-px bg-emerald-700/50" />

                    {canAny('visitors.view', 'qr-codes.view') && (
                        <NavSection label="Exhibition" defaultOpen={isActive('/visitors') || isActive('/qr-codes')} collapsed={collapsed}>
                            {can('visitors.view') && <NavItem href="/visitors" icon={UsersIcon} label="Visitors" active={isActive('/visitors')} collapsed={collapsed} />}
                            {can('qr-codes.view') && <NavItem href="/qr-codes" icon={QrCodeIcon} label="QR Codes" active={isActive('/qr-codes')} collapsed={collapsed} />}
                        </NavSection>
                    )}

                    {canAny('users.view', 'roles.view') && (
                        <NavSection label="Users & Roles" defaultOpen={isActive('/users') || isActive('/roles')} collapsed={collapsed}>
                            {can('users.view') && <NavItem href="/users" icon={UserCircleIcon} label="Users" active={isActive('/users')} collapsed={collapsed} />}
                            {can('roles.view') && <NavItem href="/roles" icon={ShieldCheckIcon} label="Roles" active={isActive('/roles')} collapsed={collapsed} />}
                        </NavSection>
                    )}

                    {canAny('suppliers.view', 'customers.view') && (
                        <NavSection label="External Parties" defaultOpen={isActive('/suppliers') || isActive('/customers')} collapsed={collapsed}>
                            {can('suppliers.view') && <NavItem href="/suppliers" icon={BuildingOfficeIcon} label="Suppliers" active={isActive('/suppliers')} collapsed={collapsed} />}
                            {can('customers.view') && <NavItem href="/customers" icon={UsersIcon} label="Customers" active={isActive('/customers')} collapsed={collapsed} />}
                        </NavSection>
                    )}

                    {canAny('materials.view', 'daily-reports.view') && (
                        <NavSection label="Operations" defaultOpen={isActive('/materials') || isActive('/daily-reports')} collapsed={collapsed}>
                            {can('materials.view') && <NavItem href="/materials" icon={ClipboardDocumentCheckIcon} label="Materials" active={isActive('/materials')} collapsed={collapsed} />}
                            {can('daily-reports.view') && <NavItem href="/daily-reports" icon={DocumentTextIcon} label="Daily Reports" active={isActive('/daily-reports')} collapsed={collapsed} />}
                        </NavSection>
                    )}

                    {canAny('timesheets.view', 'employees.view', 'work-locations.view', 'monthly-reports.view', 'employee-reports.view') && (
                        <NavSection label="Attendance" defaultOpen={isActive('/timesheets') || isActive('/employees') || isActive('/work-locations') || isActive('/monthly-reports') || isActive('/employee-reports')} collapsed={collapsed}>
                            {can('timesheets.view') && <NavItem href="/timesheets" icon={ClockIcon} label="TimeSheet" active={isActive('/timesheets')} collapsed={collapsed} />}
                            {can('employees.view') && <NavItem href="/employees" icon={UserGroupIcon} label="Employees" active={isActive('/employees')} collapsed={collapsed} />}
                            {can('work-locations.view') && <NavItem href="/work-locations" icon={MapPinIcon} label="Work Locations" active={isActive('/work-locations')} collapsed={collapsed} />}
                            {can('monthly-reports.view') && <NavItem href="/monthly-reports" icon={DocumentChartBarIcon} label="Monthly Report" active={isActive('/monthly-reports')} collapsed={collapsed} />}
                            {can('employee-reports.view') && <NavItem href="/employee-reports" icon={IdentificationIcon} label="Employee Report" active={isActive('/employee-reports')} collapsed={collapsed} />}
                        </NavSection>
                    )}

                    <div className="my-3 mx-2 h-px bg-emerald-700/50" />

                    {can('reports.view') && <NavItem href="/reports" icon={ChartBarIcon} label="Reports" active={isActive('/reports')} collapsed={collapsed} />}
                    {can('settings.view') && <NavItem href="/settings" icon={Cog6ToothIcon} label="Settings" active={isActive('/settings')} collapsed={collapsed} />}
                </nav>

                {/* Sidebar footer */}
                <div className="border-t border-emerald-700/50 p-3">
                    {user && !collapsed && (
                        <div className="flex items-center gap-3 px-2 py-2 mb-2 rounded-xl bg-emerald-700/40">
                            <div className="w-8 h-8 rounded-full bg-emerald-400/30 flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ring-2 ring-emerald-400/20">
                                {user.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-white truncate">{user.name}</p>
                                <p className="text-[10px] text-emerald-300/60 truncate">{user.email}</p>
                            </div>
                        </div>
                    )}
                    {user && collapsed && (
                        <div className="flex justify-center mb-2">
                            <div className="w-9 h-9 rounded-full bg-emerald-400/30 flex items-center justify-center text-white font-bold text-xs ring-2 ring-emerald-400/20" title={user.name}>
                                {user.name?.charAt(0).toUpperCase()}
                            </div>
                        </div>
                    )}
                    <button
                        onClick={toggleCollapse}
                        className={`hidden lg:flex items-center ${collapsed ? 'justify-center' : 'gap-2 px-3'} py-2 rounded-xl text-emerald-300/60 hover:text-white hover:bg-white/10 transition-all w-full`}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <ChevronDoubleLeftIcon className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
                        {!collapsed && <span className="text-xs font-medium">Collapse</span>}
                    </button>
                </div>
            </aside>

            {/* ===== MAIN ===== */}
            <div className={`transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}>
                {/* Header */}
                <header className="sticky top-0 z-30 h-[56px] bg-white border-b border-slate-200/70 flex items-center justify-between px-4 sm:px-6 lg:px-8 shadow-sm shadow-slate-100">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 rounded-xl hover:bg-slate-100 transition-colors">
                            <Bars3Icon className="w-5 h-5 text-slate-600" />
                        </button>
                        {collapsed && (
                            <button onClick={toggleCollapse} className="hidden lg:flex p-2 -ml-2 rounded-xl hover:bg-slate-100 transition-colors">
                                <Bars3Icon className="w-5 h-5 text-slate-500" />
                            </button>
                        )}
                        <div className="hidden sm:flex items-center text-sm text-slate-400 font-medium">
                            <HomeIcon className="w-4 h-4 mr-1.5" />
                            <Link href="/dashboard" className="hover:text-emerald-600 transition-colors">Home</Link>
                        </div>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2">
                        {/* Search */}
                        <div className="hidden md:flex items-center relative">
                            <MagnifyingGlassIcon className="absolute left-3 w-4 h-4 text-slate-400" />
                            <input type="text" placeholder="Search..." className="pl-9 pr-4 py-2 w-52 rounded-xl border border-slate-200 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-slate-50/80 placeholder-slate-400 transition-all" />
                        </div>

                        {/* Notifications */}
                        <button className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600 relative">
                            <BellIcon className="w-5 h-5" />
                        </button>

                        {/* Settings */}
                        <Link href="/settings" className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600">
                            <Cog6ToothIcon className="w-5 h-5" />
                        </Link>

                        {/* Divider */}
                        <div className="hidden sm:block w-px h-7 bg-slate-200 mx-1" />

                        {/* Profile */}
                        <div className="relative">
                            <button onClick={() => setProfileOpen(!profileOpen)} className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-slate-100 transition-colors">
                                <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                                    {user?.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="hidden sm:block text-left">
                                    <p className="text-xs font-semibold text-slate-700 leading-none">{user?.name}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">{user?.email?.split('@')[0]}</p>
                                </div>
                                <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-400 hidden sm:block transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {profileOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-slate-200/80 shadow-xl shadow-slate-200/60 z-50 p-1.5">
                                        <div className="px-3 py-2 mb-1 border-b border-slate-100">
                                            <p className="text-sm font-semibold text-slate-700">{user?.name}</p>
                                            <p className="text-xs text-slate-400">{user?.email}</p>
                                        </div>
                                        <Link href="/profile" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors">
                                            <UserCircleIcon className="w-4 h-4" /> Profile
                                        </Link>
                                        <Link href="/settings" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors">
                                            <Cog6ToothIcon className="w-4 h-4" /> Settings
                                        </Link>
                                        <div className="h-px bg-slate-100 my-1" />
                                        <Link href="/logout" method="post" as="button" className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors">
                                            <ArrowRightOnRectangleIcon className="w-4 h-4" /> Sign Out
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="p-4 sm:p-6 lg:p-8">
                    {children}
                </main>
            </div>

            {/* FAB */}
            <FAB />
        </div>
    );
}

/* =============================================
   FAB
============================================= */
function FAB() {
    const [open, setOpen] = useState(false);
    return (
        <div className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-50">
            {open && (
                <>
                    <div className="fixed inset-0" onClick={() => setOpen(false)} />
                    <div className="absolute bottom-14 right-0 mb-2 w-52 bg-white rounded-2xl shadow-2xl border border-slate-200/80 p-1.5">
                        <p className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Quick Actions</p>
                        <Link href="/visitors/create" className="flex items-center gap-3 px-3 py-2 rounded-xl text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
                            <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center"><UsersIcon className="w-3.5 h-3.5 text-emerald-600" /></div>
                            <span className="text-sm font-medium">Add Visitor</span>
                        </Link>
                        <Link href="/materials/create" className="flex items-center gap-3 px-3 py-2 rounded-xl text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                            <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center"><ClipboardDocumentCheckIcon className="w-3.5 h-3.5 text-blue-600" /></div>
                            <span className="text-sm font-medium">Requisition</span>
                        </Link>
                        <Link href="/daily-reports/create" className="flex items-center gap-3 px-3 py-2 rounded-xl text-slate-600 hover:bg-purple-50 hover:text-purple-700 transition-colors">
                            <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center"><DocumentTextIcon className="w-3.5 h-3.5 text-purple-600" /></div>
                            <span className="text-sm font-medium">Daily Report</span>
                        </Link>
                    </div>
                </>
            )}
            <button
                onClick={() => setOpen(!open)}
                className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/20
                    ${open ? 'bg-slate-700 rotate-45 shadow-slate-500/30' : 'bg-emerald-700 hover:bg-emerald-600 shadow-emerald-800/40'}`}
            >
                <PlusIcon className="w-5 h-5 text-white" />
            </button>
        </div>
    );
}
